import { toBase64url, fromBase64url } from '../../shared/crypto.js';
import type { EncryptedPayload } from '../../shared/crypto.js';
import type { Socket } from 'socket.io-client';

const ecdhParams = { name: 'ECDH', namedCurve: 'P-256' } as const;
const hkdfParams = {
  name: 'HKDF',
  hash: 'SHA-256',
  salt: new Uint8Array(32),
  info: new TextEncoder().encode('wetty-v1'),
} as const;
const aesParams = { name: 'AES-GCM', length: 256 } as const;

async function deriveSharedKey(
  myPrivate: CryptoKey,
  peerJwk: JsonWebKey,
): Promise<CryptoKey> {
  const { subtle } = window.crypto;
  const peerPublic = await subtle.importKey('jwk', peerJwk, ecdhParams, false, []);
  const rawBits = await subtle.deriveBits(
    { name: 'ECDH', public: peerPublic } as EcdhKeyDeriveParams,
    myPrivate,
    256,
  );
  const hkdfKey = await subtle.importKey('raw', rawBits, 'HKDF', false, ['deriveKey']);
  return subtle.deriveKey(hkdfParams, hkdfKey, aesParams, false, ['encrypt', 'decrypt']);
}

export async function clientHandshake(
  socket: Socket,
  serverJwk: JsonWebKey,
): Promise<CryptoKey> {
  if (!window.crypto?.subtle) {
    throw new Error('Web Crypto API unavailable — HTTPS is required for encrypted sessions');
  }
  const { subtle } = window.crypto;
  const keyPair = await subtle.generateKey(ecdhParams, true, ['deriveBits']);
  const aesKey = await deriveSharedKey(keyPair.privateKey, serverJwk);
  const clientJwk = await subtle.exportKey('jwk', keyPair.publicKey);
  socket.emit('encrypt', clientJwk);
  return aesKey;
}

export async function encrypt(key: CryptoKey, value: unknown): Promise<string> {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext,
  );
  const payload: EncryptedPayload = {
    iv: toBase64url(iv),
    ct: toBase64url(new Uint8Array(ciphertext)),
  };
  return JSON.stringify(payload);
}

export async function decrypt(key: CryptoKey, payload: string): Promise<unknown> {
  const { iv: ivB64, ct: ctB64 } = JSON.parse(payload) as EncryptedPayload;
  const plainBuf = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64url(ivB64) },
    key,
    fromBase64url(ctB64),
  );
  return JSON.parse(new TextDecoder().decode(plainBuf));
}
